/**
 * Whatever a message carries that is not text.
 *
 * Bubbles used to render message.body and nothing else, so every photo,
 * video, document and voice note arrived as an empty bubble with a
 * timestamp in it. The attach test passed because a document falls back
 * to its filename as the body — which hid the fact that nothing was
 * ever drawn.
 */

import { formatSize } from '@zivapp/shared'
import VoiceNote from './VoiceNote.jsx'
import './MessageMedia.css'

export default function MessageMedia ({ message, mine, onOpenImage }) {
  const { type, mediaUrl, mediaMeta } = message
  if (!mediaUrl) return null

  if (type === 'audio') {
    return <VoiceNote url={mediaUrl} mine={mine} durationMs={mediaMeta?.durationMs} />
  }

  if (type === 'image') {
    // Opens the in-app viewer. It used to open the raw file in a new tab,
    // which threw away the chat, the sender and the way back.
    return (
      <button
        type="button"
        className="mm__image"
        onClick={() => onOpenImage?.(message)}
        aria-label="Open photo"
      >
        <img src={mediaUrl} alt={message.body || 'Photo'} loading="lazy" />
      </button>
    )
  }

  if (type === 'video') {
    return (
      <video className="mm__video" src={mediaUrl} controls preload="metadata">
        <track kind="captions" />
      </video>
    )
  }

  // Everything else is a file to download.
  return (
    <a className="mm__doc" href={mediaUrl} target="_blank" rel="noreferrer" download>
      <span className="mm__doc-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24" fill="none">
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5Z"
            stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
          <path d="M14 3v5h5" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
        </svg>
      </span>
      <span className="mm__doc-text">
        <span className="mm__doc-name">{mediaMeta?.name ?? 'File'}</span>
        {mediaMeta?.size != null && (
          <span className="mm__doc-size">{formatSize(mediaMeta.size)}</span>
        )}
      </span>
    </a>
  )
}
